import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button_Info_White } from "@assets/images";
import DataController from "@controllers/DataController";
import { useQuery } from "@tanstack/react-query";
import { version } from "@environment/version";
import { Capacitor } from "@capacitor/core";
import { useRecoilValue } from "recoil";
import { appInfoAtom } from "@state/State";
import { AppInfo } from "@capacitor/app";
import { useLoggedIn } from "@hooks/auth/useLoggedIn";
import "./HomeComponents.css";
import classNames from "classnames";
import { APPLE_APP_URL, GOOGLE_PLAY_APP_URL } from "@constants/sites";

type AppVersions = {
  versions: {
    web: string;
    ios: string;
    android: string;
  };
};

const fetchAppVersions = async (): Promise<AppVersions> => {
  try {
    const response = await fetch("https://parafolio-config.s3.eu-west-2.amazonaws.com/app-versions/appVersions.json", {
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch app versions: ${response.status}`);
    }

    const data = await response.json();
    return data as AppVersions;
  } catch (error) {
    console.error("Error fetching app versions:", error);
    // Return a default structure to prevent errors from propagating
    return {
      versions: {
        web: "0.0.0",
        ios: "0.0.0",
        android: "0.0.0",
      },
    };
  }
};

const AppOutdatedBanner = () => {
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
  const hasAnimatedRef = useRef<boolean>(false);

  const appInfo = useRecoilValue<AppInfo | null>(appInfoAtom);
  const loggedIn = useLoggedIn();

  const { data: appVersions } = useQuery({
    queryKey: ["app-versions"],
    queryFn: fetchAppVersions,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    enabled: loggedIn && (DataController.isWeb() || appInfo !== null),
  });

  const checkIfVersionIsOutdated = (appVersions: AppVersions | undefined) => {
    if (!appVersions) return false;

    let currentAppVersion = "";
    let latestAppVersion = "";

    if (DataController.isWeb()) {
      currentAppVersion = version;
      latestAppVersion = appVersions.versions.web;
    } else if (Capacitor.getPlatform() === "ios") {
      currentAppVersion = appInfo?.version ?? "";
      latestAppVersion = appVersions.versions.ios;
    } else if (Capacitor.getPlatform() === "android") {
      currentAppVersion = appInfo?.version ?? "";
      latestAppVersion = appVersions.versions.android;
    }

    if (!currentAppVersion) return false;

    return DataController.isEarlierVersion(currentAppVersion, latestAppVersion);
  };

  const isOutdated = useMemo(() => checkIfVersionIsOutdated(appVersions), [appVersions, appInfo]);

  useEffect(() => {
    if (isOutdated && !hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
      setShouldAnimate(true);

      const timeout = setTimeout(() => {
        setShouldAnimate(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isOutdated]);

  const handleUpdate = async () => {
    if (!(await DataController.isWebAsync()) && Capacitor.getPlatform() === "android") {
      window.open(GOOGLE_PLAY_APP_URL, "_blank");
    } else if (!(await DataController.isWebAsync()) && Capacitor.getPlatform() === "ios") {
      window.open(APPLE_APP_URL, "_blank");
    } else if (await DataController.isWebAsync()) {
      window.location.reload();
    }
  };

  if (!loggedIn) return null;

  if (!appVersions) return null;
  if (!isOutdated) return null;

  return (
    <div
      className={classNames(" flex justify-center bg-menu-header py-5 sticky top-0 z-10", {
        appOutdatedBanner: shouldAnimate,
      })}
    >
      <div className="flex items-center max-w-app-column w-full pl-safe-area-l pr-safe-area-r gap-3 flex-shrink-0">
        <div className="size-6 min-w-6 overflow-hidden flex items-center justify-center">
          <img
            src={Button_Info_White}
            alt="info"
            className="w-13 h-13 object-cover"
          />
        </div>
        <div className="text-acc-17px font-acc-600 text-white">
          {"A new version of the app is available, please update. "}
          <button
            onClick={() => handleUpdate()}
            className="text-acc-17px font-bold text-white underline"
          >
            {"Click here to update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppOutdatedBanner;
